import { Button, Grid, Input, Text } from '@chakra-ui/react'
import { ColumnDef } from '@tanstack/react-table'
import { Dispatch, SetStateAction, useMemo } from 'react'

import { AdminOrganization } from '../../../services/adminOrganizationsService'
import { formatAdminDateTime } from '../../../utils/dates'

type OrganizationsAdminFiltersState = {
  name: string
  displayName: string
  auth0Id: string
}

interface OrganizationsAdminFiltersProps {
  filters: OrganizationsAdminFiltersState
  setFilters: Dispatch<SetStateAction<OrganizationsAdminFiltersState>>
  setPaginationPageIndex: (pageIndex: number) => void
}

export const OrganizationsAdminFilters = ({
  filters,
  setFilters,
  setPaginationPageIndex,
}: OrganizationsAdminFiltersProps) => (
  <Grid
    templateColumns={{ base: '1fr', md: 'repeat(3, minmax(0, 1fr)) auto' }}
    gap={3}
    mb={4}
  >
    <Input
      placeholder="Filter name"
      value={filters.name}
      onChange={event => {
        setFilters(current => ({ ...current, name: event.target.value }))
        setPaginationPageIndex(0)
      }}
    />
    <Input
      placeholder="Filter display name"
      value={filters.displayName}
      onChange={event => {
        setFilters(current => ({
          ...current,
          displayName: event.target.value,
        }))
        setPaginationPageIndex(0)
      }}
    />
    <Input
      placeholder="Filter Auth0 ID"
      value={filters.auth0Id}
      onChange={event => {
        setFilters(current => ({ ...current, auth0Id: event.target.value }))
        setPaginationPageIndex(0)
      }}
    />
    <Button
      variant="secondary"
      onClick={() => {
        setFilters({ name: '', displayName: '', auth0Id: '' })
        setPaginationPageIndex(0)
      }}
    >
      Clear
    </Button>
  </Grid>
)

export const useOrganizationsAdminColumns =
  (): ColumnDef<AdminOrganization>[] =>
    useMemo(
      () => [
        {
          accessorKey: 'name',
          header: 'Name',
          cell: info => (
            <Text fontWeight="medium">{info.getValue() as string}</Text>
          ),
          meta: {
            toText: val => val as string,
          },
        },
        {
          accessorKey: 'displayName',
          header: 'Display Name',
          cell: info => <Text>{info.getValue() as string}</Text>,
          meta: {
            toText: val => val as string,
          },
        },
        {
          accessorKey: 'auth0Id',
          header: 'Auth0 ID',
          cell: info => (
            <Text fontFamily="mono" fontSize="xs" color="presets.neutral.70">
              {info.getValue() as string}
            </Text>
          ),
          meta: {
            toText: val => val as string,
          },
        },
        {
          accessorKey: 'createdDate',
          header: 'Created',
          cell: info => (
            <Text whiteSpace="nowrap">
              {formatAdminDateTime(info.getValue() as string) ?? '—'}
            </Text>
          ),
          meta: {
            toText: val => formatAdminDateTime(val as string) ?? '—',
          },
        },
        {
          accessorKey: 'updatedDate',
          header: 'Updated',
          cell: info => (
            <Text whiteSpace="nowrap">
              {formatAdminDateTime(info.getValue() as string) ?? '—'}
            </Text>
          ),
          meta: {
            toText: val => formatAdminDateTime(val as string) ?? '—',
          },
        },
      ],
      [],
    )
